import { ReactNode, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  DAY_VALUES,
  DateRangeInput,
  DaySelector,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FileInput,
  FormField,
  Input,
  formatList,
} from "@playerelite/ui";
import type { DateRange, DayValue } from "@playerelite/ui";
import AttractorContentFields from "../../components/AttractorContentFields";
import {
  AttractorContentValue,
  contentValueFrom,
  EMPTY_CONTENT_VALUE,
  isLinkable,
  linkNoun,
} from "../../components/attractor-content";
import { KioskAttractor } from "../../types";
import { attractorTitle } from "./AttractorCampaignCard";
import {
  daysFromVisibility,
  isoFromEpoch,
  isVideoFile,
  isVideoUrl,
} from "./campaign-utils";

export interface AttractorDraft {
  range: DateRange;
  days: DayValue[];
  content: AttractorContentValue;
  /** Only used when creating; the API cannot replace an attractor's media. */
  media: File | null;
}

type DraftErrors = Partial<Record<"range" | "days" | "link" | "media", string>>;

const PORTRAIT_HINT = "Portrait only, for example 1080 × 1920.";

const mediaFormats = (videoAllowed: boolean) =>
  videoAllowed ? ["jpg", "jpeg", "png", "mp4"] : ["jpg", "jpeg", "png"];

function draftFrom(attractor: KioskAttractor | null): AttractorDraft {
  if (!attractor) {
    return {
      range: { start: "", end: "" },
      days: [...DAY_VALUES],
      content: EMPTY_CONTENT_VALUE,
      media: null,
    };
  }
  return {
    range: {
      start: isoFromEpoch(attractor.startDateEpoch),
      end: isoFromEpoch(attractor.endDateEpoch),
    },
    days: daysFromVisibility(attractor.dayVisibility),
    content: contentValueFrom(attractor),
    media: null,
  };
}

function validate(
  draft: AttractorDraft,
  isEdit: boolean,
  videoAllowed: boolean
): DraftErrors {
  const errors: DraftErrors = {};
  if (!draft.range.start || !draft.range.end) {
    errors.range = "Choose a start and end date.";
  }
  if (draft.days.length === 0) {
    errors.days = "Choose at least one day.";
  }
  const { contentType, linkedPromotion } = draft.content;
  if (isLinkable(contentType) && !linkedPromotion) {
    errors.link = `Choose the ${linkNoun(contentType)} this attractor promotes.`;
  }
  if (!isEdit && !draft.media) {
    errors.media = videoAllowed
      ? "Upload an image or video."
      : "Upload an image.";
  }
  return errors;
}

/** The old creator rejected landscape media; kiosks display portrait. Square counts as portrait. */
function readOrientation(file: File): Promise<"portrait" | "landscape"> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const settle = (width: number, height: number) => {
      URL.revokeObjectURL(url);
      resolve(width > height ? "landscape" : "portrait");
    };
    const fail = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Unreadable media"));
    };
    if (isVideoFile(file)) {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () =>
        settle(video.videoWidth, video.videoHeight);
      video.onerror = fail;
      video.src = url;
    } else {
      const img = new Image();
      img.onload = () => settle(img.naturalWidth, img.naturalHeight);
      img.onerror = fail;
      img.src = url;
    }
  });
}

function useObjectUrl(file: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  return url;
}

const MediaPreview = ({
  src,
  isVideo,
  label,
}: {
  src: string;
  isVideo: boolean;
  label: string;
}) => {
  const className =
    "h-48 w-fit max-w-full rounded-lg border border-border bg-surface-sunken object-contain";
  return isVideo ? (
    <video
      className={className}
      src={src}
      autoPlay
      loop
      muted
      playsInline
      aria-label={label}
    />
  ) : (
    <img className={className} src={src} alt={label} />
  );
};

/** Section heading inside the dialog, as in the Storybook design. */
const SectionHeader = ({
  children,
  action,
}: {
  children: ReactNode;
  action?: ReactNode;
}) => (
  <div className="flex items-center justify-between gap-2">
    <span className="font-mono text-xs font-medium uppercase tracking-wider text-fg-muted">
      {children}
    </span>
    {action}
  </div>
);

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The attractor being edited, or null to add a new one. */
  attractor: KioskAttractor | null;
  venueId: number | undefined;
  videoAllowed: boolean;
  /**
   * Saves the draft. Rejecting keeps the dialog open and shows the error's message, so throw
   * errors whose message is fit for the user.
   */
  onSubmit: (
    draft: AttractorDraft,
    reportProgress: (percent: number) => void
  ) => Promise<void>;
}

/** The Add / Edit Attractor modal from the Storybook design, wired to the kiosk attractor API. */
const AttractorFormDialog = ({
  open,
  onOpenChange,
  attractor,
  venueId,
  videoAllowed,
  onSubmit,
}: Props) => {
  const isEdit = attractor !== null;
  const [draft, setDraft] = useState<AttractorDraft>(() =>
    draftFrom(attractor)
  );
  const [mediaError, setMediaError] = useState<string>();
  // Errors are held back until the first save attempt, so a half-filled form is not all red.
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<string>();
  // The file whose orientation is being checked, so a slow check cannot overwrite a newer pick.
  const checkingFile = useRef<File | null>(null);
  const [checkingMedia, setCheckingMedia] = useState(false);
  const mediaPreviewUrl = useObjectUrl(draft.media);
  const contentRef = useRef<HTMLDivElement>(null);
  const submitErrorRef = useRef<HTMLDivElement>(null);

  // Re-seed whenever the dialog opens, so a cancelled edit is genuinely discarded.
  useEffect(() => {
    if (open) {
      setDraft(draftFrom(attractor));
      setMediaError(undefined);
      setSubmitted(false);
      setSubmitError(undefined);
      setProgress(null);
      checkingFile.current = null;
      setCheckingMedia(false);
    }
  }, [open, attractor]);

  // Save sits at the bottom of a long form and the error at the top, so bring it into view.
  useEffect(() => {
    if (submitError) {
      submitErrorRef.current?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [submitError]);

  const errors = validate(draft, isEdit, videoAllowed);
  const shown: DraftErrors = submitted ? errors : {};

  const set = <K extends keyof AttractorDraft>(
    key: K,
    value: AttractorDraft[K]
  ) => setDraft((prev) => ({ ...prev, [key]: value }));

  const onMediaChange = (file: File | null) => {
    setMediaError(undefined);
    set("media", file);
    checkingFile.current = file;
    setCheckingMedia(file !== null);
    if (!file) return;
    readOrientation(file)
      .then((orientation) => {
        if (checkingFile.current !== file || orientation === "portrait") return;
        set("media", null);
        setMediaError(
          "That file is landscape. Kiosk attractors are portrait, for example 1080 × 1920."
        );
      })
      .catch(() => {
        if (checkingFile.current !== file) return;
        set("media", null);
        setMediaError("That file couldn't be read. Try a different file.");
      })
      .finally(() => {
        if (checkingFile.current === file) setCheckingMedia(false);
      });
  };

  const save = async () => {
    setSubmitted(true);
    if (Object.keys(errors).length > 0 || mediaError) return;
    setSaving(true);
    setSubmitError(undefined);
    try {
      await onSubmit(draft, setProgress);
      onOpenChange(false);
    } catch (error) {
      setSubmitError(
        error instanceof Error && error.message
          ? error.message
          : "Something went wrong. Try again."
      );
    } finally {
      setSaving(false);
      setProgress(null);
    }
  };

  const title = attractor ? attractorTitle(attractor) : "";

  return (
    <Dialog
      open={open}
      // Closing mid-upload would orphan the request and leave the user guessing.
      onOpenChange={(next) => (saving ? undefined : onOpenChange(next))}
    >
      <DialogContent
        ref={contentRef}
        size="xl"
        // Focus the dialog itself rather than its first enabled field: that field is the date
        // range picker, which opens its calendar on focus and would cover the form.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          contentRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Attractor" : "Add Attractor"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? `Update the configuration for ${title}`
              : "Configure a new kiosk attractor"}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {submitError ? (
            <Alert ref={submitErrorRef} variant="danger" title="Couldn't save">
              {submitError}
            </Alert>
          ) : null}

          <FormField
            label="Attractor title"
            description="Coming soon. Kiosk attractors can't store a title yet."
            disabled
          >
            <Input placeholder="e.g. Summer Jackpot Splash" disabled />
          </FormField>

          <FormField label="Select date range" error={shown.range} required>
            {/* No shortcut rail: the two-month calendar only fits the xl dialog without it. */}
            <DateRangeInput
              showShortcuts={false}
              value={draft.range}
              disabled={saving}
              onValueChange={(range) => set("range", range)}
            />
          </FormField>

          <div className="flex flex-col gap-1.5">
            <SectionHeader
              action={
                <Button
                  variant="link"
                  size="sm"
                  disabled={saving}
                  onClick={() => set("days", [...DAY_VALUES])}
                >
                  Select all
                </Button>
              }
            >
              Active days
            </SectionHeader>
            <DaySelector
              aria-label="Active days"
              labels="abbr"
              // Wraps instead of overflowing the dialog at phone width.
              className="flex-wrap"
              value={draft.days}
              disabled={saving}
              invalid={Boolean(shown.days)}
              onValueChange={(days) => set("days", days)}
            />
            {shown.days ? (
              <p role="alert" className="text-xs font-medium text-danger">
                {shown.days}
              </p>
            ) : null}
          </div>

          <AttractorContentFields
            venueId={venueId}
            value={draft.content}
            onChange={(content) => set("content", content)}
            linkError={shown.link}
            disabled={saving}
          />

          <fieldset
            disabled
            className="m-0 flex flex-col gap-2 rounded-lg border border-border bg-surface-sunken p-3"
          >
            <legend className="float-left mb-1 w-full">
              <SectionHeader
                action={
                  <Badge variant="neutral" size="sm">
                    Coming soon
                  </Badge>
                }
              >
                Assign to kiosks
              </SectionHeader>
            </legend>
            <p className="text-xs text-fg-muted">
              Kiosk targeting isn't available yet. This attractor shows on every
              kiosk at the venue.
            </p>
          </fieldset>

          {attractor ? (
            <FormField
              label="Attractor media asset"
              description="Media can't be replaced on a saved attractor. Add a new attractor to use different artwork."
            >
              <MediaPreview
                src={attractor.imageUrl}
                isVideo={isVideoUrl(attractor.imageUrl)}
                label={`${title} media`}
              />
            </FormField>
          ) : (
            <FormField
              label="Attractor media asset"
              description={
                <span className="text-warning-subtle-fg">{PORTRAIT_HINT}</span>
              }
              descriptionPosition="above"
              error={mediaError ?? shown.media}
              required
            >
              <FileInput
                formats={mediaFormats(videoAllowed)}
                file={draft.media}
                disabled={saving}
                onFileChange={onMediaChange}
                onReject={() => {
                  checkingFile.current = null;
                  setCheckingMedia(false);
                  set("media", null);
                  setMediaError(
                    `That file type isn't supported. Use ${formatList(
                      mediaFormats(videoAllowed)
                    )}.`
                  );
                }}
              />
              {draft.media && mediaPreviewUrl ? (
                <MediaPreview
                  src={mediaPreviewUrl}
                  isVideo={isVideoFile(draft.media)}
                  label="Preview of the chosen media"
                />
              ) : null}
            </FormField>
          )}

          {progress !== null ? (
            <div className="flex flex-col gap-1">
              <div className="flex justify-between text-xs text-fg-muted">
                <span>Uploading media</span>
                <span className="font-mono">{progress}%</span>
              </div>
              <div
                role="progressbar"
                aria-label="Uploading media"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progress}
                className="h-2 overflow-hidden rounded-full bg-surface-sunken"
              >
                <div
                  className="h-full rounded-full bg-action transition-[width]"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ) : null}
        </DialogBody>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary" disabled={saving}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="action"
            loading={saving}
            disabled={checkingMedia}
            onClick={save}
          >
            {isEdit ? "Save Changes" : "Add Attractor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AttractorFormDialog;
